import React, { createContext, useContext, useEffect, useReducer } from "react";
import { supabase } from "../supabase";
import { useAuth } from "./AuthContext";
import { PRODUCT_PLACEHOLDER } from "../utils/productImage";

const CartContext = createContext();

// Local storage key
const CART_STORAGE_KEY = "lavvissa_cart";

// Cart reducer actions
const ACTIONS = {
  SET_CART: "set_cart",
  ADD_ITEM: "add_item",
  UPDATE_QUANTITY: "update_quantity",
  REMOVE_ITEM: "remove_item",
  CLEAR_CART: "clear_cart",
  UPDATE_CART_METADATA: "update_cart_metadata",
  SET_LOADING: "set_loading",
  SET_ERROR: "set_error",
};

// Initial state
const initialState = {
  items: [],
  itemCount: 0,
  totalPrice: 0,
  loading: false,
  error: null,
  appliedCoupon: null,
  shippingMethod: null,
};

const resolveName = (name) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  return name.fa || name.en || "";
};

// Reducer function
const cartReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_CART:
      return {
        ...state,
        ...action.payload,
        loading: false,
        error: null,
      };

    case ACTIONS.ADD_ITEM: {
      const { product, variant, quantity } = action.payload;
      const existingItemIndex = state.items.findIndex(
        (item) =>
          item.productId === product.id && item.variantId === variant.id,
      );

      let newItems;

      if (existingItemIndex >= 0) {
        // Update existing item quantity
        newItems = [...state.items];
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + quantity,
        };
      } else {
        // Add new item
        const newItem = {
          id: `${product.id}_${variant.id}`,
          productId: product.id,
          name: resolveName(product.name),
          price: product.price,
          salePrice: product.salePrice,
          image:
            product.images && product.images.length > 0
              ? product.images[0]
              : PRODUCT_PLACEHOLDER,
          variantId: variant.id,
          color: variant.color,
          colorCode: variant.colorCode,
          size: variant.size,
          quantity,
          maxQuantity: variant.stock,
        };

        newItems = [...state.items, newItem];
      }

      const { itemCount, totalPrice } = calculateCartTotals(newItems);

      return {
        ...state,
        items: newItems,
        itemCount,
        totalPrice,
        loading: false,
        error: null,
      };
    }

    case ACTIONS.UPDATE_QUANTITY: {
      const { itemId, quantity } = action.payload;
      const newItems = state.items.map((item) =>
        item.id === itemId
          ? { ...item, quantity: Math.min(quantity, item.maxQuantity) }
          : item,
      );

      const { itemCount, totalPrice } = calculateCartTotals(newItems);

      return {
        ...state,
        items: newItems,
        itemCount,
        totalPrice,
        loading: false,
        error: null,
      };
    }

    case ACTIONS.REMOVE_ITEM: {
      const itemId = action.payload;
      const newItems = state.items.filter((item) => item.id !== itemId);

      const { itemCount, totalPrice } = calculateCartTotals(newItems);

      return {
        ...state,
        items: newItems,
        itemCount,
        totalPrice,
        loading: false,
        error: null,
      };
    }

    case ACTIONS.CLEAR_CART:
      return {
        ...initialState,
        loading: false,
        error: null,
      };

    case ACTIONS.UPDATE_CART_METADATA:
      return {
        ...state,
        ...action.payload,
        loading: false,
        error: null,
      };

    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
        error: null,
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    default:
      return state;
  }
};

// Helper function to calculate cart totals
const calculateCartTotals = (items) => {
  let itemCount = 0;
  let totalPrice = 0;

  items.forEach((item) => {
    itemCount += item.quantity;
    totalPrice += (item.salePrice || item.price) * item.quantity;
  });

  return { itemCount, totalPrice };
};

// Provider component
export const CartProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Initialize cart from localStorage or Supabase
  useEffect(() => {
    const initializeCart = async () => {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });

      try {
        if (currentUser) {
          // Get cart from Supabase for logged-in users
          const { data: cartData, error } = await supabase
            .from("carts")
            .select("*")
            .eq("user_id", currentUser.id)
            .single();

          if (error && error.code !== "PGRST116") {
            // PGRST116 is "not found"
            throw error;
          }

          if (cartData) {
            dispatch({
              type: ACTIONS.SET_CART,
              payload: {
                items: cartData.items || [],
                appliedCoupon: cartData.applied_coupon || null,
                shippingMethod: cartData.shipping_method || null,
                ...calculateCartTotals(cartData.items || []),
              },
            });
          } else {
            // Try to migrate cart from localStorage if available
            const localCart = localStorage.getItem(CART_STORAGE_KEY);

            if (localCart) {
              const parsedCart = JSON.parse(localCart);

              // Save to Supabase
              const { error: insertError } = await supabase
                .from("carts")
                .insert({
                  user_id: currentUser.id,
                  items: parsedCart.items || [],
                  applied_coupon: parsedCart.appliedCoupon || null,
                  shipping_method: parsedCart.shippingMethod || null,
                  updated_at: new Date().toISOString(),
                });

              if (insertError) throw insertError;

              dispatch({
                type: ACTIONS.SET_CART,
                payload: {
                  items: parsedCart.items || [],
                  appliedCoupon: parsedCart.appliedCoupon || null,
                  shippingMethod: parsedCart.shippingMethod || null,
                  ...calculateCartTotals(parsedCart.items || []),
                },
              });

              // Clear localStorage after migration
              localStorage.removeItem(CART_STORAGE_KEY);
            } else {
              // Initialize empty cart in Supabase
              const { error: insertError } = await supabase
                .from("carts")
                .insert({
                  user_id: currentUser.id,
                  items: [],
                  updated_at: new Date().toISOString(),
                });

              if (insertError) throw insertError;

              dispatch({
                type: ACTIONS.SET_CART,
                payload: {
                  items: [],
                  itemCount: 0,
                  totalPrice: 0,
                },
              });
            }
          }
        } else {
          // For non-logged-in users, use localStorage
          const localCart = localStorage.getItem(CART_STORAGE_KEY);

          if (localCart) {
            const parsedCart = JSON.parse(localCart);

            dispatch({
              type: ACTIONS.SET_CART,
              payload: {
                items: parsedCart.items || [],
                appliedCoupon: parsedCart.appliedCoupon || null,
                shippingMethod: parsedCart.shippingMethod || null,
                ...calculateCartTotals(parsedCart.items || []),
              },
            });
          } else {
            dispatch({
              type: ACTIONS.SET_CART,
              payload: {
                items: [],
                itemCount: 0,
                totalPrice: 0,
              },
            });
          }
        }
      } catch (error) {
        console.error("Error initializing cart:", error);
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: "خطا در بارگذاری سبد خرید",
        });
      }
    };

    initializeCart();
  }, [currentUser]);

  // Save cart changes to localStorage or Supabase
  useEffect(() => {
    const saveCart = async () => {
      // Skip initial load
      if (state.loading) return;

      try {
        if (currentUser) {
          // Save to Supabase for logged-in users
          const { error } = await supabase
            .from("carts")
            .update({
              items: state.items,
              applied_coupon: state.appliedCoupon,
              shipping_method: state.shippingMethod,
              updated_at: new Date().toISOString(),
            })
            .eq("user_id", currentUser.id);

          if (error) throw error;
        } else {
          // Save to localStorage for non-logged-in users
          localStorage.setItem(
            CART_STORAGE_KEY,
            JSON.stringify({
              items: state.items,
              appliedCoupon: state.appliedCoupon,
              shippingMethod: state.shippingMethod,
            }),
          );
        }
      } catch (error) {
        console.error("Error saving cart:", error);
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: "خطا در ذخیره‌سازی سبد خرید",
        });
      }
    };

    saveCart();
  }, [
    currentUser,
    state.appliedCoupon,
    state.items,
    state.loading,
    state.shippingMethod,
  ]);

  // Cart actions
  const addItem = (product, variant, quantity = 1) => {
    if (!product || !variant) {
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: "محصول یا متغیر آن نامعتبر است",
      });
      return;
    }

    if (quantity <= 0) {
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: "تعداد باید بیشتر از صفر باشد",
      });
      return;
    }

    if (variant.stock < quantity) {
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: "موجودی محصول کافی نیست",
      });
      return;
    }

    dispatch({
      type: ACTIONS.ADD_ITEM,
      payload: { product, variant, quantity },
    });
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: "تعداد باید بیشتر از صفر باشد",
      });
      return;
    }

    const item = state.items.find((item) => item.id === itemId);

    if (!item) {
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: "آیتم در سبد خرید یافت نشد",
      });
      return;
    }

    if (quantity > item.maxQuantity) {
      dispatch({
        type: ACTIONS.SET_ERROR,
        payload: "موجودی محصول کافی نیست",
      });
      return;
    }

    dispatch({
      type: ACTIONS.UPDATE_QUANTITY,
      payload: { itemId, quantity },
    });
  };

  const removeItem = (itemId) => {
    dispatch({
      type: ACTIONS.REMOVE_ITEM,
      payload: itemId,
    });
  };

  const clearCart = () => {
    dispatch({ type: ACTIONS.CLEAR_CART });
  };

  const applyCoupon = (coupon) => {
    dispatch({
      type: ACTIONS.UPDATE_CART_METADATA,
      payload: { appliedCoupon: coupon },
    });
  };

  const removeCoupon = () => {
    dispatch({
      type: ACTIONS.UPDATE_CART_METADATA,
      payload: { appliedCoupon: null },
    });
  };

  const setShippingMethod = (method) => {
    dispatch({
      type: ACTIONS.UPDATE_CART_METADATA,
      payload: { shippingMethod: method },
    });
  };

  // Calculate final price including coupon discount if applicable
  const calculateFinalPrice = () => {
    let finalPrice = state.totalPrice;

    if (state.appliedCoupon) {
      if (state.appliedCoupon.type === "percentage") {
        finalPrice -= (finalPrice * state.appliedCoupon.value) / 100;
      } else if (state.appliedCoupon.type === "fixed") {
        finalPrice = Math.max(0, finalPrice - state.appliedCoupon.value);
      }
    }

    // Add shipping cost if shipping method is selected
    if (state.shippingMethod) {
      finalPrice += state.shippingMethod.cost;
    }

    return Math.max(0, finalPrice);
  };

  // Flexible addToCart that builds a variant object from various call signatures
  // Supports: addToCart(product, variant, quantity) — canonical
  //           addToCart(product, quantity, size, color) — from ProductDetail
  //           addToCart(product) — from ProductCard (uses first variant or default)
  const addToCart = (product, variantOrQty, sizeOrQty, color) => {
    if (!product) {
      console.error("CartContext.addToCart: product is required");
      return;
    }

    let variant;
    let qty = 1;

    // Case 1: Canonical form - addToCart(product, variantObject, quantity)
    if (variantOrQty && typeof variantOrQty === "object" && variantOrQty.id !== undefined) {
      variant = variantOrQty;
      qty = typeof sizeOrQty === "number" ? sizeOrQty : 1;
    }
    // Case 2: ProductDetail form - addToCart(product, quantity, size, color)
    else if (typeof variantOrQty === "number" && (typeof sizeOrQty === "string" || sizeOrQty === null)) {
      qty = variantOrQty;
      const size = sizeOrQty;

      // Try to find matching variant from product.variants
      const matchedVariant = (product.variants || []).find(
        (v) =>
          (v.attributes?.size === size || v.size === size) &&
          (!color || v.attributes?.color === color || v.color_name === color || v.color === color)
      );

      if (matchedVariant) {
        variant = {
          id: matchedVariant.id || `${product.id}_${size}_${color || "default"}`,
          color: color || matchedVariant.attributes?.color || matchedVariant.color_name || matchedVariant.color || "",
          colorCode: matchedVariant.attributes?.colorHex || matchedVariant.color_hex || "",
          size: size,
          stock: matchedVariant.stock_quantity || matchedVariant.attributes?.stock || 999,
        };
      } else {
        variant = {
          id: `${product.id}_${size}_${color || "default"}`,
          color: color || "",
          colorCode: "",
          size: size || "",
          stock: 999,
        };
      }
    }
    // Case 3: ProductCard form - addToCart(product) — use first variant or default
    else {
      const firstVariant = (product.variants || [])[0];

      if (firstVariant) {
        variant = {
          id: firstVariant.id || `${product.id}_default`,
          color: firstVariant.attributes?.color || firstVariant.color_name || firstVariant.color || "",
          colorCode: firstVariant.attributes?.colorHex || firstVariant.color_hex || "",
          size: firstVariant.attributes?.size || firstVariant.size || "",
          stock: firstVariant.stock_quantity || firstVariant.attributes?.stock || 999,
        };
      } else {
        variant = {
          id: `${product.id}_default`,
          color: "",
          colorCode: "",
          size: "",
          stock: 999,
        };
      }
    }

    // Normalize product price for cart item
    const normalizedProduct = {
      ...product,
      price:
        product.price ||
        product.pricing?.basePrice?.toman ||
        product.price_toman ||
        product.final_price_toman ||
        0,
      salePrice: product.salePrice || product.sale_price || null,
      name:
        typeof product.name === "object"
          ? product.name.fa || product.name.en || ""
          : product.name || "",
    };

    addItem(normalizedProduct, variant, qty);
  };

  // Context value
  const value = {
    ...state,
    addItem,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    applyCoupon,
    removeCoupon,
    setShippingMethod,
    finalPrice: calculateFinalPrice(),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom hook for using the cart context
export const useCart = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }

  return context;
};
